// dom.js — DOM-mutating helpers used across the dashboard.
//
// Anything that reads/writes the document goes here. Functions that
// transform values without touching the DOM live in src/util.js.

// $ is `document.getElementById` shortened. Returns null if the id
// doesn't exist — callers should guard.
export const $ = (id) => document.getElementById(id);

// animateValue does a flash-on-change for a number/text element.
// Callers pass the element and the new value; the function diffs
// against the current textContent and, when changed, force-reflows
// to restart the `.updated` CSS animation that highlights the
// change.
export function animateValue(el, newVal) {
  if (!el) return;
  const text = String(newVal);
  if (el.textContent === text) return;
  el.textContent = text;
  el.classList.remove("updated");
  void el.offsetWidth; // reflow
  el.classList.add("updated");
}

// patchHTML efficiently updates an element's HTML content with
// minimum visual churn:
//
//   - First render (empty container): staggered fade-in entrance
//     for each child. Tables skip the animation (it breaks table
//     layout).
//   - Same child count: morph each child's innerHTML + attributes
//     in-place. The outer DOM nodes are preserved, so the browser
//     doesn't repaint the entire list — no blink, no flash.
//   - Different child count: crossfade via an overlay clone.
//
// The `_lastHTML` cache short-circuits no-op re-renders, which the
// 5s poll tick produces frequently for unchanged pages.
//
// Page modules can rely on idempotence: patching the same HTML
// twice in a row is a single cache hit, no DOM work.
export function patchHTML(el, html) {
  if (!el) return;
  if (el._lastHTML === html) return;
  el._lastHTML = html;
  const prevCount = el.children.length;

  if (prevCount === 0) {
    // First render: staggered entrance (skip for tables — animation
    // breaks table layout).
    el.innerHTML = html;
    const hasTable = el.querySelector("table");
    if (!hasTable) {
      for (let i = 0; i < el.children.length; i++) {
        const child = el.children[i];
        child.style.opacity = "0";
        child.style.transform = "translateY(6px)";
        child.style.transition = "none";
        const delay = Math.min(i * 30, 300);
        setTimeout(() => {
          child.style.transition =
            "opacity 0.5s ease, transform 0.5s ease";
          child.style.opacity = "1";
          child.style.transform = "translateY(0)";
        }, delay);
      }
    }
    return;
  }

  // Parse new HTML into a scratch container to diff against.
  const scratch = document.createElement("div");
  scratch.innerHTML = html;
  const newCount = scratch.children.length;

  if (newCount === prevCount) {
    // Same structure: morph each child's innerHTML in-place.
    // Preserves the outer DOM nodes so the browser doesn't repaint
    // the entire list.
    for (let i = 0; i < prevCount; i++) {
      const oldChild = el.children[i];
      const newChild = scratch.children[i];
      // A different element can't be morphed into this one: replace it.
      if (oldChild.tagName !== newChild.tagName) {
        el.replaceChild(newChild.cloneNode(true), oldChild);
        continue;
      }
      // Drop attributes the new markup no longer has. Keeping them left a
      // "Loading thread…" box's class="empty" on the opened conversation,
      // which collapsed it to a single line with no messages.
      for (const name of oldChild.getAttributeNames()) {
        if (!newChild.hasAttribute(name)) oldChild.removeAttribute(name);
      }
      if (oldChild.innerHTML !== newChild.innerHTML) {
        oldChild.innerHTML = newChild.innerHTML;
      }
      // Sync attributes (class, style, data-*, etc.)
      const newAttrs = newChild.attributes;
      for (let a = 0; a < newAttrs.length; a++) {
        const attr = newAttrs[a];
        if (oldChild.getAttribute(attr.name) !== attr.value) {
          oldChild.setAttribute(attr.name, attr.value);
        }
      }
    }
  } else {
    // Structural change: crossfade via overlay.
    el.style.position = "relative";
    const overlay = el.cloneNode(true);
    overlay.style.position = "absolute";
    overlay.style.inset = "0";
    overlay.style.pointerEvents = "none";
    overlay.style.transition = "opacity 0.4s ease-out";
    overlay.style.opacity = "1";
    overlay.style.zIndex = "1";
    el.appendChild(overlay);
    // Swap real content underneath: remove all children except the
    // overlay, then add new ones.
    while (el.children.length > 1) el.removeChild(el.firstChild);
    el.insertAdjacentHTML("afterbegin", html);
    // Fade out overlay to reveal new content.
    requestAnimationFrame(() => {
      overlay.style.opacity = "0";
      setTimeout(() => overlay.remove(), 400);
    });
  }
}

// setBodyHTML is patchHTML's "no animation, no morph" sibling. Used
// by pages where the animation-heavy diff in patchHTML would clash
// with the page's own layout (Agents, Coordination — long-list
// renders where staggered fade-ins look like a flicker).
//
// The `force` option skips the cache short-circuit, useful when
// callers know the content has changed in an external way patchHTML
// can't detect.
export function setBodyHTML(el, html, opts) {
  if (!el) return;
  if (!(opts && opts.force) && el._acLastHTML === html) return;
  el._acLastHTML = html;
  el.innerHTML = html;
}

// isBodyFirstPaint returns true if setBodyHTML has not yet written
// anything to this element. Used to decide whether to paint a
// "Loading…" placeholder during the async fetch on first navigate.
export function isBodyFirstPaint(el) {
  return !el || !el._acLastHTML;
}

// loadingHTML is the placeholder a list shows until its first fetch returns.
// An empty state ("No events") before then is a claim the page can't make yet.
export function loadingHTML(what) {
  return '<div class="empty" style="opacity:1;animation:none;"><div class="empty-title">Loading ' + what + "…</div></div>";
}
